import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Check, Copy, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

export type DayKey = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

const DAYS: { value: DayKey; label: string; short: string }[] = [
  { value: "monday", label: "Monday", short: "Mon" },
  { value: "tuesday", label: "Tuesday", short: "Tue" },
  { value: "wednesday", label: "Wednesday", short: "Wed" },
  { value: "thursday", label: "Thursday", short: "Thu" },
  { value: "friday", label: "Friday", short: "Fri" },
  { value: "saturday", label: "Saturday", short: "Sat" },
  { value: "sunday", label: "Sunday", short: "Sun" },
];

const DAY_LABELS: Record<DayKey, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const formatTimeTo12h = (time24: string): string => {
  const [hStr, mStr] = time24.split(":");
  let h = parseInt(hStr) || 0;
  const m = mStr || "00";
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
};

interface DuplicateDaySlotsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentWeeklySchedule: Record<string, string[]>;
  onApply: (targetDay: DayKey, newSlots: string[]) => void;
}

const DuplicateDaySlotsModal = ({
  open,
  onOpenChange,
  currentWeeklySchedule,
  onApply,
}: DuplicateDaySlotsModalProps) => {
  const [sourceDay, setSourceDay] = useState<DayKey | "">("");
  const [targetDays, setTargetDays] = useState<DayKey[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Days that have at least one slot (available as source)
  const daysWithSlots = useMemo(() => {
    return DAYS.filter(d => (currentWeeklySchedule[d.value] || []).length > 0);
  }, [currentWeeklySchedule]);

  // Slots from the source day
  const sourceSlots = useMemo(() => {
    if (!sourceDay) return [];
    return currentWeeklySchedule[sourceDay] || [];
  }, [sourceDay, currentWeeklySchedule]);

  // Target days that already have slots
  const targetDaysWithExistingSlots = useMemo(() => {
    return targetDays.filter(d => (currentWeeklySchedule[d] || []).length > 0);
  }, [targetDays, currentWeeklySchedule]);

  const canApply = sourceDay && targetDays.length > 0 && sourceSlots.length > 0;

  const toggleTargetDay = (day: DayKey) => {
    setTargetDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const handleApply = () => {
    if (!canApply) return;
    if (targetDaysWithExistingSlots.length > 0) {
      setConfirmOpen(true);
    } else {
      executeApply();
    }
  };

  const executeApply = () => {
    targetDays.forEach(day => {
      onApply(day, [...sourceSlots]);
    });
    const dayNames = targetDays.map(d => DAY_LABELS[d]).join(", ");
    toast.success(`Copied ${sourceSlots.length} slot${sourceSlots.length !== 1 ? "s" : ""} from ${DAY_LABELS[sourceDay as DayKey]} to ${dayNames}`);
    setConfirmOpen(false);
    resetAndClose();
  };

  const resetAndClose = () => {
    setSourceDay("");
    setTargetDays([]);
    onOpenChange(false);
  };

  // Select all available target days (excluding source)
  const selectAllTargets = () => {
    const allTargets = DAYS.filter(d => d.value !== sourceDay).map(d => d.value);
    setTargetDays(allTargets);
  };

  const clearAllTargets = () => {
    setTargetDays([]);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(val) => {
        if (!val) {
          setSourceDay("");
          setTargetDays([]);
        }
        onOpenChange(val);
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-4 h-4" />
              Duplicate day schedule
            </DialogTitle>
            <DialogDescription>
              Copy all time slots from one day to other days of the week.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Source day */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Copy from</Label>
              {daysWithSlots.length === 0 ? (
                <div className="px-3 py-2.5 border rounded-md bg-muted/50 text-sm text-muted-foreground">
                  No days have time slots yet. Add slots to a day first.
                </div>
              ) : (
                <Select
                  value={sourceDay}
                  onValueChange={(v) => {
                    setSourceDay(v as DayKey);
                    // Remove new source from target if already selected
                    setTargetDays(prev => prev.filter(d => d !== v));
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a day to copy from" />
                  </SelectTrigger>
                  <SelectContent>
                    {daysWithSlots.map((d) => (
                      <SelectItem key={d.value} value={d.value}>
                        <span className="flex items-center gap-2">
                          {d.label}
                          <span className="text-muted-foreground text-xs">
                            ({(currentWeeklySchedule[d.value] || []).length} slot{(currentWeeklySchedule[d.value] || []).length !== 1 ? "s" : ""})
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Source slots preview */}
            {sourceDay && sourceSlots.length > 0 && (
              <div className="rounded-lg bg-muted/40 border px-3.5 py-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Slots to copy ({sourceSlots.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {sourceSlots.map(time => (
                    <span
                      key={time}
                      className="inline-flex items-center px-2.5 py-1 bg-primary/10 text-primary border border-primary/20 rounded-md text-xs font-medium"
                    >
                      {formatTimeTo12h(time)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Arrow indicator */}
            {sourceDay && sourceSlots.length > 0 && (
              <div className="flex items-center justify-center">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <div className="h-px w-8 bg-border" />
                  <ArrowRight className="w-4 h-4" />
                  <div className="h-px w-8 bg-border" />
                </div>
              </div>
            )}

            {/* Target days */}
            {sourceDay && sourceSlots.length > 0 && (
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Paste to</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllTargets}
                      className="text-xs text-primary hover:underline font-medium"
                    >
                      Select all
                    </button>
                    {targetDays.length > 0 && (
                      <button
                        type="button"
                        onClick={clearAllTargets}
                        className="text-xs text-muted-foreground hover:underline font-medium"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {DAYS.filter(d => d.value !== sourceDay).map((d) => {
                    const isSelected = targetDays.includes(d.value);
                    const existingCount = (currentWeeklySchedule[d.value] || []).length;
                    return (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => toggleTargetDay(d.value)}
                        className={cn(
                          "flex items-center gap-3 px-3.5 py-2.5 rounded-lg border text-left transition-all duration-150",
                          isSelected
                            ? "border-primary/40 bg-primary/5 ring-1 ring-primary/20"
                            : "border-border hover:border-muted-foreground/30 hover:bg-muted/30"
                        )}
                      >
                        <div className={cn(
                          "h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center transition-colors",
                          isSelected
                            ? "bg-primary border-primary text-primary-foreground"
                            : "border-muted-foreground/30"
                        )}>
                          {isSelected && <Check className="h-3 w-3" />}
                        </div>
                        <span className="text-sm font-medium flex-1">{d.label}</span>
                        {existingCount > 0 && (
                          <span className={cn(
                            "text-xs px-2 py-0.5 rounded-full font-medium",
                            isSelected
                              ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                              : "bg-muted text-muted-foreground"
                          )}>
                            {existingCount} existing
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Summary */}
            {canApply && (
              <div className="rounded-lg bg-primary/5 border border-primary/20 px-3.5 py-2.5">
                <p className="text-sm text-primary font-medium">
                  {sourceSlots.length} slot{sourceSlots.length !== 1 ? "s" : ""} will be copied to {targetDays.length} day{targetDays.length !== 1 ? "s" : ""}
                  {targetDaysWithExistingSlots.length > 0 && (
                    <span className="text-amber-600 dark:text-amber-400">
                      {" "}(replacing slots on {targetDaysWithExistingSlots.map(d => DAY_LABELS[d]).join(", ")})
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex flex-row justify-end gap-2 shrink-0">
            <Button variant="ghost" onClick={resetAndClose}>
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={!canApply}
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" />
              Duplicate slots
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm overwrite dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace existing slots?</AlertDialogTitle>
            <AlertDialogDescription>
              {targetDaysWithExistingSlots.length === 1
                ? `${DAY_LABELS[targetDaysWithExistingSlots[0]]} already has ${(currentWeeklySchedule[targetDaysWithExistingSlots[0]] || []).length} slot${(currentWeeklySchedule[targetDaysWithExistingSlots[0]] || []).length !== 1 ? "s" : ""}. `
                : `${targetDaysWithExistingSlots.map(d => DAY_LABELS[d]).join(", ")} already have existing slots. `
              }
              This will replace all existing slots on {targetDaysWithExistingSlots.length === 1 ? "that day" : "those days"} with the slots from {sourceDay ? DAY_LABELS[sourceDay as DayKey] : ""}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeApply}
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DuplicateDaySlotsModal;
